import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  keyframes,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import { tokens } from "../theme/tokens";

const scaleIn = keyframes`
  0% {
    transform: scale(0.6);
    opacity: 0;
  }
  60% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const steps = [
  {
    icon: AssignmentTurnedInOutlinedIcon,
    title: "Registration received",
    description: "Your signup has been submitted.",
  },
  {
    icon: VerifiedUserOutlinedIcon,
    title: "Account verification",
    description: "Our team is reviewing your profile.",
  },
  {
    icon: SupportAgentOutlinedIcon,
    title: "We'll connect soon",
    description: "We'll reach out once your account is verified.",
  },
];

const SignupThankYou = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const clientName = location.state?.clientName?.trim();

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(rgba(6, 20, 40, 0.72), rgba(10, 30, 64, 0.82)), url('https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        overflow: "hidden",
        px: 2,
      }}
    >
      <Container
        maxWidth="xs"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          px: 0,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            width: "100%",
            maxWidth: 400,
            textAlign: "center",
            borderRadius: { xs: "14px", sm: "18px" },
            backgroundColor: "rgba(255, 255, 255, 0.96)",
            backdropFilter: "blur(12px)",
            boxShadow: tokens.shadowLg,
            overflow: "hidden",
            position: "relative",
            animation: `${fadeUp} 0.5s ease-out`,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${tokens.brand}, ${tokens.accent}, ${tokens.highlight})`,
            },
          }}
        >
          <Box sx={{ mb: 1.5, display: "flex", justifyContent: "center" }}>
            <img
              src="/images/Wordmark.svg"
              alt="TripTrip"
              style={{ height: 22, width: "auto" }}
            />
          </Box>

          <Box
            sx={{
              width: 52,
              height: 52,
              mx: "auto",
              mb: 1.5,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${tokens.successSoft}, ${tokens.accentSoft})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: `${scaleIn} 0.5s ease-out`,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 30, color: tokens.success }} />
          </Box>

          <Typography
            variant="h5"
            component="h1"
            sx={{
              mb: 1,
              fontFamily: tokens.fontDisplay,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              background: `linear-gradient(45deg, ${tokens.brand} 20%, ${tokens.accent} 90%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1.2rem", sm: "1.35rem" },
              lineHeight: 1.3,
            }}
          >
            Thank you for joining TripTrip!
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.55, px: 0.5 }}
          >
            {clientName ? (
              <>
                Welcome, <strong style={{ color: tokens.brand }}>{clientName}</strong>.
                {" "}Our team will verify your account and connect with you soon.
              </>
            ) : (
              "Our team will verify your account and connect with you soon."
            )}
          </Typography>

          <Stack spacing={1} sx={{ mb: 2, textAlign: "left" }}>
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Box
                  key={step.title}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    p: 1.25,
                    borderRadius: `${tokens.radiusSm}px`,
                    bgcolor: index === 0 ? tokens.successSoft : tokens.surface,
                    border: `1px solid ${index === 0 ? "rgba(13, 122, 92, 0.2)" : tokens.border}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: index === 0 ? tokens.success : tokens.accentSoft,
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 16,
                        color: index === 0 ? "#fff" : tokens.accent,
                      }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: tokens.brand, display: "block", lineHeight: 1.3 }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1.35, display: "block" }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>

          <Button
            variant="text"
            onClick={() => navigate("/contact-us")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8125rem",
              color: tokens.accent,
              py: 0.5,
              "&:hover": {
                bgcolor: tokens.accentSoft,
              },
            }}
          >
            Need help? Contact us
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default SignupThankYou;
